import { z } from 'zod';
import { isWithinBounds, tablesOverlap } from '../../utils/floorPlan';
import { zText } from '../primitives';
import { labelPlacementSchema, labelPlacementStrictSchema } from './labelPlacementSchema';
import { tablePlacementSchema, tablePlacementStrictSchema } from './tablePlacementSchema';

/** A named table layout for the restaurant (e.g. "Dining room", "Outside"). */
export const floorPlanSchema = z.object({
  name: z.string(),
  restaurantId: z.string(),
  /** Ordering for the POS floor-plan toggle */
  displayIndex: z.number().default(0),
  tables: z.array(tablePlacementSchema).default([]),
  /** Reference-only text annotations on the canvas */
  labels: z.array(labelPlacementSchema).default([]),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type FloorPlan = z.output<typeof floorPlanSchema>;

/** Strict schema */
export const floorPlanStrictSchema = floorPlanSchema
  // Use base schemas on the root object; cross-item rules are in superRefine below.
  // Child arrays use strict schemas so per-item validation runs at parse time.
  .safeExtend({
    name: zText.pipe(z.string().min(1).max(40)),
    tables: z.array(tablePlacementStrictSchema),
    labels: z.array(labelPlacementStrictSchema),
  })

  /** Validate: table labels are unique within the plan */
  .superRefine((plan, ctx) => {
    const seen = new Set<string>();
    plan.tables.forEach((table) => {
      const key = table.label.trim().toLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate table name "${table.label}"`,
          path: ['tables', table.uuid, 'label'],
        });
      }
      seen.add(key);
    });
  })

  /** Validate: tables stay within the grid */
  .superRefine((plan, ctx) => {
    plan.tables.forEach((table) => {
      if (!isWithinBounds(table)) {
        ctx.addIssue({
          code: 'custom',
          message: `Table "${table.label}" is outside the floor plan`,
          path: ['tables', table.uuid],
        });
      }
    });
  })

  /** Validate: tables do not overlap each other */
  .superRefine((plan, ctx) => {
    const tables = plan.tables;
    for (let i = 0; i < tables.length; i++) {
      for (let j = i + 1; j < tables.length; j++) {
        const a = tables[i];
        const b = tables[j];
        if (a && b && tablesOverlap(a, b)) {
          ctx.addIssue({
            code: 'custom',
            message: `Tables "${a.label}" and "${b.label}" overlap`,
            path: ['tables', b.uuid],
          });
        }
      }
    }
  })

  /** Validate: labels stay within the grid (reference-only, so no overlap/uniqueness rules) */
  .superRefine((plan, ctx) => {
    plan.labels.forEach((label) => {
      if (!isWithinBounds(label)) {
        ctx.addIssue({
          code: 'custom',
          message: `Label "${label.text}" is outside the floor plan`,
          path: ['labels', label.uuid],
        });
      }
    });
  });

/** Convex schema */
export const floorPlanCvxSchema = floorPlanSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type FloorPlanCvx = z.output<typeof floorPlanCvxSchema>;
